import { z } from 'zod';
import type { SubscriptionTierCode } from './subscription.js';
import { SportNameSchema } from './profile.js';

/**
 * F39 — Featured Placements (Paid) frozen API contracts.
 *
 * Venues buy fixed-price-by-the-week placements from an admin-managed inventory
 * catalogue (homepage carousel, sport-in-location featured row, top-of-search
 * slot, sponsored notifications, banners). Admin pre-approves every placement's
 * creative before it goes live. Billing routes through F23's
 * `commission_statements` engine as a new `source = 'venue_placement'` line
 * item (see `commission.ts COMMISSION_SOURCES`), settled off-platform.
 *
 * Tier (F38) drives a monthly credit pool, an out-of-pocket discount, and a
 * per-placement-type restriction (sponsored notifications + banners are Pro+).
 * The tier perks are CODE-DEFINED in `FEATURED_PLACEMENT_TIER_PERKS` (the F38
 * `TIER_FEATURE_FLAGS` precedent — NOT catalogue-stored) and SNAPSHOTTED onto
 * the placement at purchase (the F05 `fx_rate_id` / F35 commission-rate
 * snapshot rule) so a later tier change never rewrites a purchased placement's
 * economics.
 *
 * All monetary fields are USD minor units (integer, cents).
 *
 * See plans/39_F39_featured_placements.md §0/§3/§4/§9/§10. Wave-2 sub-agents
 * develop against these contracts in parallel — DO NOT rename/drop/add fields
 * without escalating to the orchestrator first (contract-amendment protocol).
 *
 * 🔴 NOTE: F38's subscription tier (`SubscriptionTierCode ∈ {free,starter,pro,
 * premium}`) is a SEPARATE concept from F11's `venues.tier` listing/claim tier
 * (`tier_1/tier_2/tier_3`). Never conflate or touch `venues.tier`.
 */

// ─── Enum constants ──────────────────────────────────────────────────────────

/**
 * The 7 placement types (§3a — seeded into `placement_inventory_config`,
 * admin-editable). Stable identifiers, used as the catalogue PK + the
 * `placements.placement_type` FK + the `/public/placements?slot=` resolver key.
 */
export const PLACEMENT_TYPES = [
  'homepage_carousel',
  'sport_in_location',
  'top_of_search',
  'sponsored_notification',
  'banner_open_games',
  'banner_trainer_marketplace',
  'banner_team_profile',
  // F39↔F41 bounded merge — admin platform-promote of a sponsored tournament.
  'sponsored_tournament',
] as const;
export type PlacementType = (typeof PLACEMENT_TYPES)[number];
export const PlacementTypeSchema = z.enum(PLACEMENT_TYPES);

/**
 * Inventory scope kinds (§3a). `global` (one inventory pool platform-wide) ·
 * `sport_location` (scoped per sport × city) · `search` (scoped per search
 * context = sport × city). Drives the scarcity scope key + the
 * scope-consistency CHECK on `placements`.
 */
export const PLACEMENT_SCOPE_KINDS = ['global', 'sport_location', 'search', 'tournament'] as const;
export type PlacementScopeKind = (typeof PLACEMENT_SCOPE_KINDS)[number];
export const PlacementScopeKindSchema = z.enum(PLACEMENT_SCOPE_KINDS);

/**
 * Per-type tier restriction (D5). `null` = all tiers; `'pro_plus'` = gated on
 * F38 `features.featured_placements` (Pro+). Modelled as a 1-value enum so the
 * catalogue CHECK + Zod stay extensible.
 */
export const PLACEMENT_TIER_RESTRICTIONS = ['pro_plus'] as const;
export type PlacementTierRestriction = (typeof PLACEMENT_TIER_RESTRICTIONS)[number];
export const PlacementTierRestrictionSchema = z.enum(PLACEMENT_TIER_RESTRICTIONS);

/**
 * Placement status state machine (§5). `pending_approval → scheduled → active
 * → expired` (the happy path) with `rejected` (terminal, no F23 line item) +
 * `cancelled` (admin-only, D4) terminal escapes. Only `expired` placements bill.
 */
export const PLACEMENT_STATUSES = [
  'pending_approval',
  'scheduled',
  'active',
  'expired',
  'cancelled',
  'rejected',
] as const;
export type PlacementStatus = (typeof PLACEMENT_STATUSES)[number];
export const PlacementStatusSchema = z.enum(PLACEMENT_STATUSES);

/** Append-only `placement_events.event_type` ledger kinds (§4e / D11). */
export const PLACEMENT_EVENT_TYPES = ['impression', 'click', 'conversion'] as const;
export type PlacementEventType = (typeof PLACEMENT_EVENT_TYPES)[number];
export const PlacementEventTypeSchema = z.enum(PLACEMENT_EVENT_TYPES);

/** The B2C surface an event fired from (analytics dimension). */
export const PLACEMENT_EVENT_SURFACES = ['mobile', 'portal', 'marketing'] as const;
export type PlacementEventSurface = (typeof PLACEMENT_EVENT_SURFACES)[number];
export const PlacementEventSurfaceSchema = z.enum(PLACEMENT_EVENT_SURFACES);

/** Impression viewport position (impression rows only — analytics). */
export const PLACEMENT_VIEWPORT_POSITIONS = ['above_fold', 'below_fold'] as const;
export type PlacementViewportPosition = (typeof PLACEMENT_VIEWPORT_POSITIONS)[number];
export const PlacementViewportPositionSchema = z.enum(PLACEMENT_VIEWPORT_POSITIONS);

/**
 * The public slot-resolver `slot` query values (§9). The six RENDER surfaces
 * (the carousel + the two discovery injections + the three banners). Note
 * `sponsored_notification` is NOT a slot — it's delivered via F26, not rendered.
 */
export const PLACEMENT_SLOTS = [
  'homepage_carousel',
  'sport_in_location',
  'top_of_search',
  'banner_open_games',
  'banner_trainer_marketplace',
  'banner_team_profile',
  // F39↔F41 bounded merge — the sponsored-tournament render slot (admin promote).
  // Resolves `sponsored_tournament`-type placements (scope_kind='tournament') +
  // enriches each via the F41 TournamentLookupForSponsored; never organic-backfilled.
  'sponsored_tournament',
] as const;
export type PlacementSlot = (typeof PLACEMENT_SLOTS)[number];
export const PlacementSlotSchema = z.enum(PLACEMENT_SLOTS);

// ─── Constants (§0 / §3) ─────────────────────────────────────────────────────

/** D-scheduling — minimum lead time before `period_start` (admin approval fits). */
export const PLACEMENT_MIN_LEAD_HOURS = 24 as const;
/** D-scheduling — purchasable up to this many days in advance. */
export const PLACEMENT_MAX_ADVANCE_DAYS = 90 as const;
/** D-sponsored-notif-cap — at most this many sponsored notifications per user per week. */
export const SPONSORED_NOTIF_WEEKLY_CAP = 2 as const;
/** D11 / D-conversion-scope — the click→booking attribution window. */
export const PLACEMENT_CONVERSION_WINDOW_HOURS = 24 as const;
/** D2 — a placement spans 1..N contiguous weeks (the CHECK upper bound). */
export const PLACEMENT_MIN_WEEKS = 1 as const;
export const PLACEMENT_MAX_WEEKS = 52 as const;
/** §8b — per-(user/session × placement × type) daily soft caps (inflation guard). */
export const PLACEMENT_IMPRESSION_DAILY_CAP = 50 as const;
export const PLACEMENT_CLICK_DAILY_CAP = 10 as const;
/** Creative field bounds (§4b — moderated copy). */
export const PLACEMENT_CREATIVE_COPY_MAX = 280 as const;
export const PLACEMENT_CREATIVE_HEADLINE_MAX = 80 as const;
/** Mandatory-reason bounds (reject/cancel/inventory-edit/credit-grant). */
export const PLACEMENT_REASON_MAX = 500 as const;

// ─── Tier-perk matrix (code-defined — §3b, mirrors F38 TIER_FEATURE_FLAGS) ────

/**
 * Per-tier placement perks (D6/D7/D-multi-placement-cap). Keyed by
 * `SubscriptionTierCode`. The catalogue stores per-type pricing + scarcity; the
 * perks live here as a frozen record. F39's purchase path resolves these from
 * the venue's `effectiveTierCode` (lock-down aware — F38 D9) then SNAPSHOTS the
 * resolved credit pool + discount onto the `placements` row.
 *
 *   - monthlyCreditMinor: per F38 billing period, non-rolling (D6). Spent
 *     before out-of-pocket. Free $0 / Starter $0 / Pro $50 / Premium $200.
 *   - discountPercent: out-of-pocket discount, applied AFTER credits (D7).
 *     Free 0% / Starter 0% / Pro 10% / Premium 20%.
 *   - perTypeMaxActive: simultaneous pending_approval+scheduled+active
 *     placements per type (D-multi-placement-cap). 2/3/5/10.
 */
export const FEATURED_PLACEMENT_TIER_PERKS: Readonly<
  Record<
    SubscriptionTierCode,
    Readonly<{
      monthlyCreditMinor: number;
      discountPercent: number;
      perTypeMaxActive: number;
    }>
  >
> = Object.freeze({
  free: Object.freeze({ monthlyCreditMinor: 0, discountPercent: 0, perTypeMaxActive: 2 }),
  starter: Object.freeze({ monthlyCreditMinor: 0, discountPercent: 0, perTypeMaxActive: 3 }),
  pro: Object.freeze({ monthlyCreditMinor: 5000, discountPercent: 10, perTypeMaxActive: 5 }), // $50
  premium: Object.freeze({ monthlyCreditMinor: 20000, discountPercent: 20, perTypeMaxActive: 10 }), // $200
});

// ─── Inventory catalogue seed (§3a / §3b — admin-editable in the CMS) ─────────

/**
 * One seed row for the `placement_inventory_config` catalogue (§3a). The 7
 * rows are inserted idempotently by migration 0034. All prices USD minor units.
 */
export type PlacementInventorySeedRow = {
  readonly placementType: PlacementType;
  readonly displayName: string;
  readonly scopeKind: PlacementScopeKind;
  readonly basePriceMinor: number;
  /** Max simultaneous active+scheduled per scope-week; null = uncapped. */
  readonly scarcityCap: number | null;
  readonly minLeadHours: number;
  readonly maxAdvanceDays: number;
  /** null = all tiers; 'pro_plus' = D5 gate. */
  readonly tierRestriction: PlacementTierRestriction | null;
  readonly requiresCreative: boolean;
  readonly sortOrder: number;
};

export const PLACEMENT_INVENTORY_SEED: ReadonlyArray<PlacementInventorySeedRow> = [
  {
    placementType: 'homepage_carousel',
    displayName: 'Homepage carousel',
    scopeKind: 'global',
    basePriceMinor: 4000,
    scarcityCap: 5,
    minLeadHours: PLACEMENT_MIN_LEAD_HOURS,
    maxAdvanceDays: PLACEMENT_MAX_ADVANCE_DAYS,
    tierRestriction: null,
    requiresCreative: false,
    sortOrder: 0,
  },
  {
    placementType: 'sport_in_location',
    displayName: 'Sport-in-location featured row',
    scopeKind: 'sport_location',
    basePriceMinor: 2500,
    scarcityCap: 3,
    minLeadHours: PLACEMENT_MIN_LEAD_HOURS,
    maxAdvanceDays: PLACEMENT_MAX_ADVANCE_DAYS,
    tierRestriction: null,
    requiresCreative: false,
    sortOrder: 1,
  },
  {
    placementType: 'top_of_search',
    displayName: 'Top-of-search slot',
    scopeKind: 'search',
    basePriceMinor: 2000,
    scarcityCap: 1,
    minLeadHours: PLACEMENT_MIN_LEAD_HOURS,
    maxAdvanceDays: PLACEMENT_MAX_ADVANCE_DAYS,
    tierRestriction: null,
    requiresCreative: false,
    sortOrder: 2,
  },
  {
    placementType: 'sponsored_notification',
    displayName: 'Sponsored notification (weekly)',
    scopeKind: 'sport_location',
    basePriceMinor: 3000,
    scarcityCap: null,
    minLeadHours: PLACEMENT_MIN_LEAD_HOURS,
    maxAdvanceDays: PLACEMENT_MAX_ADVANCE_DAYS,
    tierRestriction: 'pro_plus',
    requiresCreative: true,
    sortOrder: 3,
  },
  {
    placementType: 'banner_open_games',
    displayName: 'Banner — open games',
    scopeKind: 'global',
    basePriceMinor: 1500,
    scarcityCap: 1,
    minLeadHours: PLACEMENT_MIN_LEAD_HOURS,
    maxAdvanceDays: PLACEMENT_MAX_ADVANCE_DAYS,
    tierRestriction: 'pro_plus',
    requiresCreative: true,
    sortOrder: 4,
  },
  {
    placementType: 'banner_trainer_marketplace',
    displayName: 'Banner — trainer marketplace',
    scopeKind: 'global',
    basePriceMinor: 1500,
    scarcityCap: 1,
    minLeadHours: PLACEMENT_MIN_LEAD_HOURS,
    maxAdvanceDays: PLACEMENT_MAX_ADVANCE_DAYS,
    tierRestriction: 'pro_plus',
    requiresCreative: true,
    sortOrder: 5,
  },
  {
    placementType: 'banner_team_profile',
    displayName: 'Banner — team profile',
    scopeKind: 'global',
    basePriceMinor: 1000,
    scarcityCap: 3,
    minLeadHours: PLACEMENT_MIN_LEAD_HOURS,
    maxAdvanceDays: PLACEMENT_MAX_ADVANCE_DAYS,
    tierRestriction: 'pro_plus',
    requiresCreative: true,
    sortOrder: 6,
  },
];

// ─── Price calculator (§3c — the contract the portal + server both implement) ─

/**
 * The §3c price-calculator breakdown. All amounts USD minor units. The portal's
 * live Stepper calculator (`POST .../calculate`) and the server-side purchase
 * transaction BOTH compute this; the purchase recomputes server-side (never
 * trusts the client) and SNAPSHOTS gross / discountPercent / creditApplied /
 * netCharge onto the `placements` row.
 */
export type PlacementChargeBreakdown = {
  readonly basePriceMinor: number;
  readonly weeks: number;
  readonly grossMinor: number;
  readonly discountPercent: number;
  readonly discountMinor: number;
  readonly afterDiscountMinor: number;
  readonly creditAppliedMinor: number;
  /** The out-of-pocket amount that bills via F23. */
  readonly netChargeMinor: number;
  readonly currency: string;
};

/**
 * Inputs to {@link computePlacementCharge}. `creditPoolRemainingMinor` is the
 * venue's remaining (non-rolling) credit for the current F38 billing period;
 * `discountPercent` is the tier discount (§3b — resolved from the venue's
 * `effectiveTierCode`). Both are passed in resolved (the pure function does no
 * IO).
 */
export type ComputePlacementChargeInput = {
  readonly basePriceMinor: number;
  readonly weeks: number;
  readonly discountPercent: number;
  readonly creditPoolRemainingMinor: number;
  readonly currency?: string;
};

/**
 * §3c calculator — pure function. The single source of truth for placement
 * pricing math, shared by the portal calculator endpoint + the server-side
 * purchase transaction.
 *
 *     gross          = basePrice × weeks
 *     discount       = floor(gross × discountPercent / 100)   // D7 — half-down via floor
 *     afterDiscount  = gross − discount
 *     creditApplied  = min(creditPoolRemaining, afterDiscount)
 *     netCharge      = afterDiscount − creditApplied          // out-of-pocket → bills via F23
 *
 * Integer math throughout (USD minor units). The discount uses `Math.floor`
 * per the plan §3c formula (rounding in the venue's favour — never charges
 * more than the exact discounted price). All inputs must be non-negative
 * integers; `discountPercent` in [0, 100]; `weeks` in [1, 52].
 *
 * @throws Error when inputs violate the integer / range contract.
 */
export function computePlacementCharge(
  input: ComputePlacementChargeInput,
): PlacementChargeBreakdown {
  const { basePriceMinor, weeks, discountPercent, creditPoolRemainingMinor } = input;
  const currency = input.currency ?? 'USD';

  if (!Number.isInteger(basePriceMinor) || basePriceMinor < 0) {
    throw new Error(
      `computePlacementCharge: basePriceMinor must be a non-negative integer, got ${basePriceMinor}`,
    );
  }
  if (!Number.isInteger(weeks) || weeks < PLACEMENT_MIN_WEEKS || weeks > PLACEMENT_MAX_WEEKS) {
    throw new Error(
      `computePlacementCharge: weeks must be an integer in [${PLACEMENT_MIN_WEEKS}, ${PLACEMENT_MAX_WEEKS}], got ${weeks}`,
    );
  }
  if (!Number.isInteger(discountPercent) || discountPercent < 0 || discountPercent > 100) {
    throw new Error(
      `computePlacementCharge: discountPercent must be an integer in [0, 100], got ${discountPercent}`,
    );
  }
  if (!Number.isInteger(creditPoolRemainingMinor) || creditPoolRemainingMinor < 0) {
    throw new Error(
      `computePlacementCharge: creditPoolRemainingMinor must be a non-negative integer, got ${creditPoolRemainingMinor}`,
    );
  }

  const grossMinor = basePriceMinor * weeks;
  const discountMinor = Math.floor((grossMinor * discountPercent) / 100);
  const afterDiscountMinor = grossMinor - discountMinor;
  const creditAppliedMinor = Math.min(creditPoolRemainingMinor, afterDiscountMinor);
  const netChargeMinor = afterDiscountMinor - creditAppliedMinor;

  return {
    basePriceMinor,
    weeks,
    grossMinor,
    discountPercent,
    discountMinor,
    afterDiscountMinor,
    creditAppliedMinor,
    netChargeMinor,
    currency,
  };
}

// ─── Read-side projections ───────────────────────────────────────────────────

/**
 * One row of the `placement_inventory_config` catalogue (admin-editable — D8).
 * `updatedAt` is the If-Match version source for CMS edits.
 */
export type PlacementInventoryConfig = {
  readonly placementType: PlacementType;
  readonly displayName: string;
  readonly scopeKind: PlacementScopeKind;
  readonly basePriceMinor: number;
  readonly priceCurrency: string;
  readonly scarcityCap: number | null;
  readonly minLeadHours: number;
  readonly maxAdvanceDays: number;
  readonly tierRestriction: PlacementTierRestriction | null;
  readonly requiresCreative: boolean;
  readonly active: boolean;
  readonly sortOrder: number;
  readonly countryCode: string;
  readonly createdAt: string;
  readonly updatedAt: string;
};

/**
 * One `placements` purchase row (§4b — the load-bearing entity). The
 * `*_at_purchase` / `*Minor` snapshot fields are IMMUTABLE after insert
 * (F05/F35 pattern). `scopeKind` is denormalised from the catalogue at insert
 * so the scope-consistency CHECK is pure-SQL.
 */
export type Placement = {
  readonly id: string;
  /** Null for a `tournament`-scoped (admin platform-promote) placement; set otherwise. */
  readonly venueId: string | null;
  readonly placementType: PlacementType;
  readonly scopeKind: PlacementScopeKind;
  readonly scopeSport: string | null;
  readonly scopeCityCode: string | null;
  /** F39↔F41 — the targeted tournament when `scopeKind === 'tournament'`, else null. */
  readonly scopeTournamentId: string | null;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly weeks: number;
  readonly status: PlacementStatus;
  // snapshot (immutable after insert)
  readonly tierCodeAtPurchase: SubscriptionTierCode;
  readonly basePriceMinor: number;
  readonly grossMinor: number;
  readonly discountPercent: number;
  readonly creditAppliedMinor: number;
  readonly netChargeMinor: number;
  readonly priceCurrency: string;
  // creative (moderated — D10)
  readonly creativeImageS3Key: string | null;
  readonly creativeCopy: string | null;
  readonly creativeHeadline: string | null;
  // lifecycle audit
  /** Null for an admin platform-promote (tournament scope); set for venue purchases. */
  readonly purchasedByUserId: string | null;
  readonly approvedByAdminId: string | null;
  readonly approvedAt: string | null;
  readonly rejectedReason: string | null;
  readonly cancelledByAdminId: string | null;
  readonly cancelledReason: string | null;
  /** D9 — the monthly `venue_placement` statement this rolled into. */
  readonly billedStatementId: string | null;
  readonly countryCode: string;
  readonly createdAt: string;
  readonly updatedAt: string;
};

/**
 * One `placement_credits` per-venue-per-period tier credit-pool row (§4c — D6).
 * `remaining = grantedMinor − consumedMinor + Σ adjustments.deltaMinor`, never
 * below 0 (enforced at the service layer).
 */
export type PlacementCredits = {
  readonly id: string;
  readonly venueId: string;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly grantedMinor: number;
  readonly consumedMinor: number;
  readonly tierCodeAtGrant: SubscriptionTierCode;
  readonly countryCode: string;
  readonly createdAt: string;
  readonly updatedAt: string;
};

/**
 * One `placement_credit_adjustments` admin goodwill-grant row (§4d — D4).
 * positive `deltaMinor` = grant (increases remaining); negative = clawback.
 */
export type PlacementCreditAdjustment = {
  readonly id: string;
  readonly venueId: string;
  readonly periodStart: string;
  readonly deltaMinor: number;
  readonly reason: string;
  readonly actorAdminId: string | null;
  readonly countryCode: string;
  readonly createdAt: string;
};

/**
 * The owner-facing credit-pool view (`GET .../placements/credits`). Bundles the
 * current-period pool + the adjustment history (D-credit-grant rendering).
 */
export type PlacementCreditSummary = {
  readonly venueId: string;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly grantedMinor: number;
  readonly consumedMinor: number;
  readonly adjustmentsTotalMinor: number;
  readonly remainingMinor: number;
  readonly tierCodeAtGrant: SubscriptionTierCode;
  readonly adjustments: ReadonlyArray<PlacementCreditAdjustment>;
};

/**
 * One `placement_events` append-only ledger row (§4e — D11). Conversion rows
 * carry `bookingId` + `attributedClickEventId`; impression/click rows carry
 * `surface` + `surfaceSection` + (impressions) `viewportPosition`.
 */
export type PlacementEvent = {
  readonly id: string;
  readonly placementId: string;
  readonly eventType: PlacementEventType;
  readonly userId: string | null;
  readonly sessionId: string | null;
  readonly idempotencyKey: string | null;
  readonly surface: PlacementEventSurface | null;
  readonly surfaceSection: string | null;
  readonly viewportPosition: PlacementViewportPosition | null;
  readonly bookingId: string | null;
  readonly attributedClickEventId: string | null;
  readonly venueId: string;
  readonly scopeSport: string | null;
  readonly countryCode: string;
  readonly createdAt: string;
};

/**
 * Per-placement performance tally (CTR/CPC/CPA dashboard). Returned by the
 * portal performance endpoint + the admin analytics surface.
 */
export type PlacementPerformanceSummary = {
  readonly placementId: string;
  readonly impressions: number;
  readonly clicks: number;
  readonly conversions: number;
  /** clicks / impressions (0 when no impressions). */
  readonly ctr: number;
  /** netChargeMinor / clicks (USD minor units; 0 when no clicks). */
  readonly cpcMinor: number;
  /** netChargeMinor / conversions (USD minor units; 0 when no conversions). */
  readonly cpaMinor: number;
  readonly spendMinor: number;
  readonly currency: string;
};

// ─── Zod input schemas ───────────────────────────────────────────────────────

/**
 * The scope sub-object shared by purchase + calculate. `sport`+`city` are
 * required for `sport_location`/`search` types, null/absent for `global` —
 * the service cross-validates against the catalogue's `scopeKind` (the SQL
 * scope-consistency CHECK is the backstop).
 */
export const PlacementScopeInputSchema = z
  .object({
    sport: SportNameSchema.optional(),
    cityCode: z.string().trim().min(1).max(64).optional(),
    /**
     * F39↔F41 — the targeted tournament for a `tournament`-scoped purchase (the
     * organizer-PAID promote path). Required for the `sponsored_tournament`
     * placement type; null/absent for the venue-scoped types. The buyer must OWN
     * the tournament (`tournaments.organizer_b2b_account_id` == the buyer venue's
     * `owner_b2b_account_id`) — validated at the service layer.
     */
    tournamentId: z.string().uuid().optional(),
  })
  .strict();
export type PlacementScopeInput = z.infer<typeof PlacementScopeInputSchema>;

/**
 * `POST /me/venues/:venueId/placements` body (§7a). `Idempotency-Key` header
 * carries the F16 replay key (NOT in the body). `periodStart` is the first
 * Monday 00:00 Asia/Beirut of the booked span; the server derives `periodEnd`
 * from `weeks`. Creative is required at the service layer for
 * `requiresCreative` types.
 */
export const PurchasePlacementInputSchema = z
  .object({
    type: PlacementTypeSchema,
    weeks: z.number().int().min(PLACEMENT_MIN_WEEKS).max(PLACEMENT_MAX_WEEKS),
    periodStart: z.string().datetime(),
    scope: PlacementScopeInputSchema.optional(),
    creativeImageS3Key: z.string().trim().min(1).max(512).optional(),
    creativeCopy: z.string().trim().min(1).max(PLACEMENT_CREATIVE_COPY_MAX).optional(),
    creativeHeadline: z.string().trim().min(1).max(PLACEMENT_CREATIVE_HEADLINE_MAX).optional(),
  })
  .strict();
export type PurchasePlacementInput = z.infer<typeof PurchasePlacementInputSchema>;

/**
 * `POST /me/venues/:venueId/placements/calculate` body — live price preview
 * (§3c). No write. Same scope shape as purchase.
 */
export const CalculatePlacementInputSchema = z
  .object({
    type: PlacementTypeSchema,
    weeks: z.number().int().min(PLACEMENT_MIN_WEEKS).max(PLACEMENT_MAX_WEEKS),
    scope: PlacementScopeInputSchema.optional(),
  })
  .strict();
export type CalculatePlacementInput = z.infer<typeof CalculatePlacementInputSchema>;

/**
 * `PUT /admin/placement-inventory/:type` body — CMS edit (§9). `If-Match`
 * header + mandatory reason. At least one editable field must be present.
 */
export const EditPlacementInventoryInputSchema = z
  .object({
    basePriceMinor: z.number().int().min(0).optional(),
    scarcityCap: z.number().int().min(1).nullable().optional(),
    minLeadHours: z.number().int().min(0).optional(),
    maxAdvanceDays: z.number().int().min(1).optional(),
    tierRestriction: PlacementTierRestrictionSchema.nullable().optional(),
    active: z.boolean().optional(),
    reason: z.string().trim().min(10).max(PLACEMENT_REASON_MAX),
  })
  .strict()
  .refine(
    (v) =>
      v.basePriceMinor !== undefined ||
      v.scarcityCap !== undefined ||
      v.minLeadHours !== undefined ||
      v.maxAdvanceDays !== undefined ||
      v.tierRestriction !== undefined ||
      v.active !== undefined,
    { message: 'At least one editable field must be provided' },
  );
export type EditPlacementInventoryInput = z.infer<typeof EditPlacementInventoryInputSchema>;

/** `POST /admin/placements/:id/approve` body — no fields (audit reason optional). */
export const ApprovePlacementInputSchema = z
  .object({
    notes: z.string().trim().max(PLACEMENT_REASON_MAX).optional(),
  })
  .strict();
export type ApprovePlacementInput = z.infer<typeof ApprovePlacementInputSchema>;

/** `POST /admin/placements/:id/reject` body — mandatory reason (§5 / §9). */
export const RejectPlacementInputSchema = z
  .object({
    reason: z.string().trim().min(10).max(PLACEMENT_REASON_MAX),
  })
  .strict();
export type RejectPlacementInput = z.infer<typeof RejectPlacementInputSchema>;

/**
 * `POST /admin/placements/:id/cancel` body — D4 admin override-cancel.
 * Mandatory reason + optional goodwill credit-grant (`grantCreditMinor`).
 */
export const CancelPlacementInputSchema = z
  .object({
    reason: z.string().trim().min(10).max(PLACEMENT_REASON_MAX),
    grantCreditMinor: z.number().int().min(0).optional(),
  })
  .strict();
export type CancelPlacementInput = z.infer<typeof CancelPlacementInputSchema>;

/**
 * `POST /admin/placements/promote-tournament` body — the F39↔F41 bounded merge
 * admin platform-promote path. Creates a `scope_kind='tournament'`,
 * `placement_type='sponsored_tournament'` placement targeting a tournament,
 * landing `active` immediately (admin-promoted — NO organizer billing/credit
 * path; that fuller F39-for-organizers feature stays deferred). `weeks` sets the
 * render window; `periodStart` defaults to now server-side when omitted.
 */
export const PromoteTournamentPlacementInputSchema = z
  .object({
    tournamentId: z.string().uuid(),
    weeks: z.number().int().min(PLACEMENT_MIN_WEEKS).max(PLACEMENT_MAX_WEEKS).optional(),
    periodStart: z.string().datetime().optional(),
    reason: z.string().trim().min(10).max(PLACEMENT_REASON_MAX),
  })
  .strict();
export type PromoteTournamentPlacementInput = z.infer<typeof PromoteTournamentPlacementInputSchema>;

/**
 * `POST /admin/venues/:venueId/placement-credits/grant` body — D4 goodwill
 * grant → a positive `placement_credit_adjustments` row. Mandatory reason.
 * `periodStart` targets the credit period (ISO; defaults to current period
 * server-side when omitted).
 */
export const GrantPlacementCreditInputSchema = z
  .object({
    deltaMinor: z.number().int(),
    reason: z.string().trim().min(10).max(PLACEMENT_REASON_MAX),
    periodStart: z.string().datetime().optional(),
  })
  .strict()
  .refine((v) => v.deltaMinor !== 0, { message: 'deltaMinor must be non-zero' });
export type GrantPlacementCreditInput = z.infer<typeof GrantPlacementCreditInputSchema>;

/**
 * `POST /public/placements/:placementId/events` body (§8b). Anonymous OK;
 * rate-limited. `idempotencyKey` is the client-built
 * `<userId|sessionId>:<placementId>:<eventType>:<dayBucket>` (the inflation
 * guard); the 24h replay window is enforced service-side.
 */
export const IngestPlacementEventInputSchema = z
  .object({
    eventType: z.enum(['impression', 'click']),
    surface: PlacementEventSurfaceSchema,
    surfaceSection: z.string().trim().min(1).max(64).optional(),
    viewportPosition: PlacementViewportPositionSchema.optional(),
    sessionId: z.string().trim().min(1).max(128).optional(),
    idempotencyKey: z.string().trim().min(1).max(256),
  })
  .strict();
export type IngestPlacementEventInput = z.infer<typeof IngestPlacementEventInputSchema>;

/**
 * `GET /public/placements` slot-resolver query (§9). `slot` is required; the
 * sport/city narrow `sport_location`/`search` slots; `limit` caps the returned
 * items (paid + organic backfill).
 */
export const PlacementResolverQuerySchema = z
  .object({
    slot: PlacementSlotSchema,
    sport: SportNameSchema.optional(),
    city: z.string().trim().min(1).max(64).optional(),
    /** F39↔F41 — narrows the `sponsored_tournament` slot to one tournament (optional). */
    tournamentId: z.string().uuid().optional(),
    limit: z.coerce.number().int().min(1).max(20).optional(),
  })
  .strict();
export type PlacementResolverQuery = z.infer<typeof PlacementResolverQuerySchema>;

// ─── Error codes ─────────────────────────────────────────────────────────────

/**
 * F39 placement error vocabulary (~16 codes). Service maps to HTTP:
 *   - `*_NOT_FOUND` → 404
 *   - `*_STALE` → 409 (If-Match mismatch)
 *   - `SCOPE_SOLD_OUT` / `*_CAP_REACHED` / `*_REPLAY_*` → 409
 *   - `*_REQUIRES_PRO` / `*_FORBIDDEN` → 403
 *   - everything else → 422
 */
export const PLACEMENT_ERROR_CODES = [
  'PLACEMENT_NOT_FOUND',
  'PLACEMENT_INVENTORY_NOT_FOUND',
  'PLACEMENT_INVENTORY_STALE',
  'PLACEMENT_INVENTORY_INACTIVE',
  'PLACEMENT_TYPE_REQUIRES_PRO',
  'PLACEMENT_TYPE_CAP_REACHED',
  'SCOPE_SOLD_OUT',
  'PLACEMENT_LEAD_TIME',
  'PLACEMENT_TOO_FAR_AHEAD',
  'PLACEMENT_SCOPE_REQUIRED',
  'PLACEMENT_SCOPE_FORBIDDEN',
  'PLACEMENT_CREATIVE_REQUIRED',
  'PLACEMENT_NOT_PENDING_APPROVAL',
  'PLACEMENT_NOT_CANCELLABLE',
  'PLACEMENT_IDEMPOTENT_REPLAY_PAYLOAD_MISMATCH',
  'PLACEMENT_FORBIDDEN',
  'PLACEMENT_INVALID_PERIOD',
  // F39↔F41 bounded merge — admin tournament platform-promote.
  'PLACEMENT_TOURNAMENT_NOT_FOUND',
  'PLACEMENT_TOURNAMENT_NOT_ELIGIBLE',
  // F39↔F41 organizer-PAID promote — the buyer venue's account does not own the
  // tournament, or the owning account has no billable venue (e.g. a federation
  // `account_kind='organizer'` with no venue → 403, use the admin promote path).
  'PLACEMENT_TOURNAMENT_NOT_OWNED',
  'PLACEMENT_TOURNAMENT_SCOPE_REQUIRED',
] as const;
export type PlacementErrorCode = (typeof PLACEMENT_ERROR_CODES)[number];

// ─── F61 (B18) — venue card embedded in `GET /public/placements` items ─────────

/**
 * The display data a slot item needs, embedded so clients no longer fetch
 * `GET /public/venues/:id` once per item. Present (an object) on venue-centric
 * items whose venue is active; `null` when the venue is not active (clients
 * drop the item); absent on `sponsored_tournament` items. Older app versions
 * ignore the field and keep their per-item fetch.
 */
export interface PlacementVenueCard {
  readonly slug: string;
  readonly name: string;
  /** F11 listing tier (`tier_1` … `tier_3`). */
  readonly tier: string;
  /** Primary sport first (the venue detail's order). */
  readonly sports: readonly string[];
  /** F09 v2 venue tick (boolean only on public payloads). */
  readonly verified: boolean;
  /** The first approved photo's card variant, else its original URL. */
  readonly coverPhotoUrl: string | null;
  readonly cazaCode: string;
  readonly cityCode: string;
  readonly countryCode: string;
}
